import { EPTSessionType, EPTTimestampType } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { SiteUrls } from '@tableau/shared-urls';

import { Location } from '../../commonInfra/utils/Location';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { getSessionManagementServiceInstance } from '../services/SessionManagementService';

import { BindUI } from '../common/UIBinding';
import { ContentSuggestionTestId } from '../components/ContentSuggestion';
import { ContentDefinitions } from '../models/ContentDefinitions';
import { ContentSuggestionDataItem, ContentType, OmniBoxItemType } from '../models/Types';
import { SearchService } from '../services/SearchService';
import { SearchEPTService } from '../services/SearchEPTService';
import { IKeyboardNavigableItem, QuickSearchModule } from './QuickSearchModule';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { searchTelemetry } from '../components/SearchTelemetry';
import { activeDataQualityWarningIcon, certifiedIcon, severeDataqualityWarningIcon } from '../../img/svgIcons';
import { OmniboxConfigurationService } from '../OmniboxConfigurationService';

export const ContentSuggestionImpressionType = 'contentSuggestion';

export interface ContentSuggestionItem extends IKeyboardNavigableItem {
    id: number;
    luid: string;
    titleHtml: string;
    contentType: ContentType,
    ownerHtml: string;
    hiddenOwner: boolean;
    supportingData: string;
    icon: string;
    extraIcons?: string[];
    data: ContentSuggestionDataItem;
    url: string;
    hasDraft: boolean;
}

export class ContentSuggestionViewModel extends QuickSearchModule {
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public contentSuggestionItems: ContentSuggestionItem[];
    @BindUI() public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    public maxContentSuggestionCount: number;
    public displayedInEmptyStateUI = false;

    // clearDataOnMount: determines whether content suggestions should be cleared after mounting component.
    // Since EmptyStateContentSuggestion only queries for suggestions once, we should never clear its suggestions.
    protected clearDataOnMount = true;

    public initialize(): void {
        this.maxContentSuggestionCount = OmniboxConfigurationService.instance.configuration.contentSuggestionMaxCount;
    }

    public processSearchTerm(searchTerm: string): void {
        if (searchTerm) {
            const searchEPT = FeatureFlagService.instance.getValue(FeatureFlag.searchEPT);
            if (searchEPT) {
                SearchEPTService.instance.mark(EPTSessionType.contentSuggestion, searchTerm, EPTTimestampType.sendingRequestsToServerSide);
            }
            SearchService.instance.getContentSuggestions(searchTerm).then(result => {
                if (!this.isUnmounted && FeatureFlagService.instance.getValue(FeatureFlag.searchProductMetrics)) {
                    searchTelemetry.sendSearchImpressionEvent(searchTerm, ContentSuggestionImpressionType);
                }
                if (searchEPT) {
                    SearchEPTService.instance.mark(EPTSessionType.contentSuggestion, searchTerm, EPTTimestampType.gotResponseFromServerSide);
                }
                this.parseSuggestionData(result);

                if (!this.isUnmounted && searchEPT) {
                    SearchEPTService.instance.end(EPTSessionType.contentSuggestion, this.omniboxViewModel.searchTerm);
                }
            });
        } else {
            this.contentSuggestionItems = [];
        }
    }

    public onMount(): void {
        if (this.clearDataOnMount) {
            this.contentSuggestionItems = null;
        }
        super.onMount();
    }

    public selectItemByKeyboard(): void {
        const currentItem = this.currentKeyboardNavigableItem as ContentSuggestionItem;
        this.onItemClicked(currentItem, this.contentSuggestionItems.indexOf(currentItem));
    }

    public isKeyboardNavigable() {
        return !this.isUnmounted && this.contentSuggestionItems && this.contentSuggestionItems.length > 0;
    }

    public buildKeyboardNavigationPathInOwnModule() {
        if (this.isKeyboardNavigable()) {
            this.getFirstKeyboardNavigableItem().up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();

            for (let i = 0; i < this.contentSuggestionItems.length - 1; i++) {
                const currentItem = this.contentSuggestionItems[i];
                const nextItem = this.contentSuggestionItems[i + 1];
                this.linkTwoKeyboardNavigableItems(currentItem, nextItem, true);
            }

            this.getLastKeyboardNavigableItem().down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
        }
    }

    public getFirstKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.contentSuggestionItems[0];
    }

    public getLastKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.contentSuggestionItems[this.contentSuggestionItems.length - 1];
    }

    public onItemClicked(item: ContentSuggestionItem, index: number) {
        this.omniboxViewModel.onResultItemSelected(OmniBoxItemType.contentSuggestionItem, this.contentSuggestionItems.length, index, item.id.toString());

        if (!OmniboxConfigurationService.instance.configuration.onContentSuggestionItemClicked) {
            Location.instance.setUrl(item.url);
        } else {
            OmniboxConfigurationService.instance.configuration.onContentSuggestionItemClicked(item.contentType, item.data);
        }
    }

    protected parseSuggestionData(result: ContentSuggestionDataItem[]) {
        if (!result) {
            this.contentSuggestionItems = [];
            return;
        }

        const hiddenOwner = getSessionManagementServiceInstance().isLimitedUserVisibilityChannel();
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });

        Promise.all(result.map(async (item, index) => {
            return await this.processSuggestionDataItem(item, hiddenOwner, urls, index);
        })).then((processedResults) => {
            this.contentSuggestionItems = processedResults.filter(item => item).slice(0, this.maxContentSuggestionCount);
        }).catch((err) => { this.contentSuggestionItems = []; })
    }

    private async processSuggestionDataItem(item: ContentSuggestionDataItem, hiddenOwner: boolean, urls: SiteUrls, index: number): Promise<ContentSuggestionItem | null> {
        const contentType = item.content.type as ContentType;
        const extraIcons: string[] = [];
        if (OmniboxConfigurationService.instance.configuration.shouldShowSuggestion) {
            const shouldShowSuggestion = await OmniboxConfigurationService.instance.configuration.shouldShowSuggestion(item)
            if (!shouldShowSuggestion) return null;
        }

        if (this.isDataCatalogContentType(contentType)) {
            if (item.content.isCertified) {
                extraIcons.push(certifiedIcon);
            }
            if (item.content.hasActiveDataQualityWarning) {
                extraIcons.push(item.content.hasSevereDataQualityWarning ? severeDataqualityWarningIcon : activeDataQualityWarningIcon);
            }
        }

        if (ContentDefinitions[contentType]) {
            return {
                id: item.content.id,
                luid: item.content.luid,
                titleHtml: item.highlights?.title ? item.highlights.title.join('...') : (item.content.title || ''),
                contentType: contentType,
                contentTypeText: ContentDefinitions[contentType].getName(),
                hiddenOwner,
                ownerHtml: hiddenOwner ? Messages.hiddenUser()
                    : (item.highlights?.owner ? item.highlights.owner.join('...') : (item.content.ownerName || '')),
                supportingData: this.getSupportingData(item),
                icon: ContentDefinitions[contentType].icon,
                url: ContentDefinitions[contentType].getUrl(urls, item),
                hasDraft: item.content.hasDraft,
                data: item,
                extraIcons,
                ownerModule: this,
                getIdentifier: () => ContentSuggestionTestId + index.toString()
            } as ContentSuggestionItem;
        }
        return null;
    }

    private getSupportingData(item: ContentSuggestionDataItem): string {
        switch (item.content.type) {
            case ContentType.view:
                return item.content.hitsMediumSpanTotal ? Messages.omniboxRecentViews({ NUMBER: item.content.hitsMediumSpanTotal, NUMBER_LOC: Formatters.formatCount(item.content.hitsMediumSpanTotal) }) : '';
            case ContentType.workbook:
                return item.content.hitsMediumSpanTotal ? Messages.omniboxRecentViews({ NUMBER: item.content.hitsMediumSpanTotal, NUMBER_LOC: Formatters.formatCount(item.content.hitsMediumSpanTotal) }) : '';
            case ContentType.metric:
                return item.content.modifiedTime ? this.getProperAgoTimeString(item.content.modifiedTime) : '';
            case ContentType.collection:
                return item.content.itemCount !== undefined ? Messages.omniboxItems({ NUMBER: item.content.itemCount, NUMBER_LOC: Formatters.formatCount(item.content.itemCount) }) : '';
            case ContentType.project:
                break;
            case ContentType.lens:
                break;
            case ContentType.unifieddatasource:
                return item.content.connectedWorkbooksCount !== undefined ? Messages.omniboxConnectedWorkbookCount({ NUMBER: item.content.connectedWorkbooksCount, NUMBER_LOC: Formatters.formatCount(item.content.connectedWorkbooksCount) }) : '';
            case ContentType.flow:
                break;
            case ContentType.datarole:
                break;
            case ContentType.virtualconnection:
                break;
            case ContentType.table:
                return item.content.connectedWorkbooksCount !== undefined ? Messages.omniboxConnectedWorkbookCount({ NUMBER: item.content.connectedWorkbooksCount, NUMBER_LOC: Formatters.formatCount(item.content.connectedWorkbooksCount) }) : '';
            case ContentType.database:
                return item.content.connectedWorkbooksCount !== undefined ? Messages.omniboxConnectedWorkbookCount({ NUMBER: item.content.connectedWorkbooksCount, NUMBER_LOC: Formatters.formatCount(item.content.connectedWorkbooksCount) }) : '';
        }

        return '';
    }

    private getProperAgoTimeString(dateTimeString: string) {
        const now = new Date();
        const then = new Date(dateTimeString);

        const seconds = (now.getTime() - then.getTime()) / 1000;

        if (seconds < 60) {
            return Messages.omniboxRefreshedSecondsAgo({ NUMBER: seconds, NUMBER_LOC: Formatters.formatCount(seconds) });
        } else if (seconds < 60 * 60) {
            const minutes = Math.round(seconds / 60);
            return Messages.omniboxRefreshedMinutesAgo({ NUMBER: minutes, NUMBER_LOC: Formatters.formatCount(minutes) });
        } else if (seconds < 60 * 60 * 24) {
            const hours = Math.round(seconds / 60 / 60);
            return Messages.omniboxRefreshedHoursAgo({ NUMBER: hours, NUMBER_LOC: Formatters.formatCount(hours) });
        } else if (seconds < 60 * 60 * 24 * 30) {
            const days = Math.round(seconds / 60 / 60 / 24);
            return Messages.omniboxRefreshedDaysAgo({ NUMBER: days, NUMBER_LOC: Formatters.formatCount(days) });
        }

        const months = Math.round(seconds / 60 / 60 / 24 / 30);
        return Messages.omniboxRefreshedMonthsAgo({ NUMBER: months, NUMBER_LOC: Formatters.formatCount(months) });
    }

    private isDataCatalogContentType(contentType: ContentType) {
        return contentType === ContentType.unifieddatasource
            || contentType === ContentType.database
            || contentType === ContentType.table
            || contentType === ContentType.virtualconnection;
    }
}
